import React, { useState } from 'react';
import { Package, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const DeclarationActionCard = ({ 
  ticket,
  currentUser,
  onDeclarationSubmit 
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Check if current user is the allocated recipient
  const isAllocatedRecipient = (() => {
    try {
      const cf = ticket?.custom_fields ? 
        (typeof ticket.custom_fields === 'string' ? JSON.parse(ticket.custom_fields) : ticket.custom_fields) 
        : {};
      const allocatedUserId = cf?.integration_results?.user_created?.user_id;
      return allocatedUserId && String(currentUser?.user_id) === String(allocatedUserId);
    } catch (e) {
      console.error('Error checking allocated recipient:', e);
      return false;
    }
  })();

  // Don't show if not allocated recipient or already declared
  if (!isAllocatedRecipient || ticket?.asset_declared === 'yes') {
    return null;
  }

  const handleSubmit = async () => {
    if (!confirmed) {
      toast.warning('Please confirm that you have received the allocated assets');
      return;
    }

    setSubmitting(true);
    try {
      await onDeclarationSubmit();
      toast.success('Asset declaration submitted successfully');
    } catch (error) {
      console.error('Error submitting declaration:', error);
      toast.error('Failed to submit declaration');
    } finally {
      setSubmitting(false);
    }
  };

  // Get allocated assets for display
  const allocatedAssets = (() => {
    try {
      const cf = ticket?.custom_fields ? 
        (typeof ticket.custom_fields === 'string' ? JSON.parse(ticket.custom_fields) : ticket.custom_fields) 
        : {};
      const allocated = cf?.integration_results?.asset_created?.allocated_asset_ids;
      return Array.isArray(allocated) ? allocated : [];
    } catch (e) {
      return [];
    }
  })();

  return (
    <div className="bg-white rounded-lg shadow-sm border-2 border-green-500">
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
            <span className="text-green-800 text-sm font-medium">
              Asset Declaration Required
            </span>
          </div>
        </div>

        {/* Allocated Assets Display */}
        {allocatedAssets.length > 0 ? (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm font-semibold text-gray-900 mb-3">Please confirm receipt of:</p>
            <ul className="space-y-2">
              {allocatedAssets.map((assetId) => (
                <li key={assetId} className="flex items-center text-sm text-gray-700">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                  <span className="font-medium">Asset ID:</span>
                  <span className="ml-2">{assetId}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-sm text-gray-600">
            <p>Assets have been allocated to you for this request.</p>
          </div>
        )}

        {/* Confirmation Checkbox */}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="declaration-confirm"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
          />
          <label htmlFor="declaration-confirm" className="ml-2 text-sm text-gray-700">
            I confirm that I have received the assets listed above
          </label>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitting || !confirmed}
          className="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Submitting...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Submit Declaration
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DeclarationActionCard;
